import { css } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";

const innerPaddingHorizontal = 6;
const innerPaddingVertical = 4;
const outerPaddingHorizontal = 4;
const outerPaddingVertical = 5;

export const root = css({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  backgroundColor: "white",
});

export const content = css({
  flex: 1,
  minHeight: 0,
});

export const addElements = css({
  marginLeft: "auto",
  alignSelf: "center",
});

export const tabsContainer = () => (theme: ExactTheme) =>
  css({
    display: "flex",
    borderBottom: `1px solid ${theme.colorsV2.grey[100]}`,
    padding: "0 10px",
    height: "35px",
    width: "100%",
    flexShrink: 0,
  });
export const tabsWrapper = css`
  max-width: calc(100% - 30px);
  display: flex;
  position: relative;
  z-index: 1;
  top: 1px;
  overflow: hidden;
  flex-shrink: 0;
`;

export const tabTitle = (theme: ExactTheme) =>
  css({
    padding: `${innerPaddingVertical}px ${innerPaddingHorizontal}px`,
    borderRadius: theme.borderRadiusV2.primary,
  });

export const item =
  (args: { isActive: boolean; showIndicator: boolean; isClosable: boolean }) =>
  (theme: ExactTheme) => {
    const { isActive, showIndicator, isClosable } = args;
    const { colorsV2, fontSizesV2, colors } = theme;

    return [
      css({
        position: "relative",
        padding: `${outerPaddingVertical}px ${outerPaddingHorizontal}px`,
        flexShrink: 0,
        outline: "none",
        background: isActive ? "white" : "transparent",
        color: colorsV2.fg.default,
        fontSize: fontSizesV2.body,
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: isActive ? colorsV2.grey[100] : "transparent",
        borderBottom: "none",
        cursor: "pointer",

        ":disabled": {
          color: colorsV2.fg.muted,
          cursor: "not-allowed",
        },

        ":not(:disabled):hover": {
          ".tab-separator": {
            display: "none",
          },
          ".tab-title": {
            backgroundColor: isActive ? "transparent" : colors.canvas.default,
          },
        },

        ":after": {
          content: isActive ? '""' : "none",
          position: "absolute",
          height: 1,
          left: 0,
          right: 0,
          top: "100%",
          backgroundColor: "white",
        },

        ...(showIndicator && {
          "::before": {
            content: '""',
            position: "absolute",
            top: 4,
            right: 4,
            width: 4,
            height: 4,
            borderRadius: "50%",
            backgroundColor: colorsV2.orange[500],
          },
        }),
      }),
      isActive &&
        css({
          backgroundColor: theme.colors.canvas.subtle,
          color: theme.colors.fg.default,
          border: `1px solid ${theme.colorsV2.grey[100]}`,
          borderBottom: "none",

          ":after": {
            content: '""',
            position: "absolute",
            top: "-1px",
            height: "2px",
            left: "-1px",
            right: "-1px",
            backgroundColor: theme.colors.accent.fg,
          },
        }),
      isClosable &&
        css`
          .tab-title {
            display: block;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
          }

          &:hover {
            .tab-close-icon {
              display: flex;
            }

            .tab-title {
              width: calc(100% - 8px); // hack to truncate the tab title
            }
          }
        `,
    ];
  };

export const separator = (theme: ExactTheme) =>
  css({
    position: "absolute",
    right: -1,
    top: "50%",
    transformOrigin: "center",
    transform: "translateY(-50%)",
    height: 16,
    width: 1,
    backgroundColor: theme.colorsV2.grey[100],
  });

export const closeButton = (theme: ExactTheme) => css`
  display: none;
  position: absolute;
  padding: 0;
  border: none;
  background: transparent;
  top: 50%;
  transform-origin: center;
  transform: translateY(-50%);
  right: 8px;
  width: 16px;
  height: 16px;
  color: ${theme.colors.gray[300]};

  :hover {
    color: ${theme.colors.danger.fg};
  }
`;

export const unsavedChangesIndicator =
  ({ visible }: { visible: boolean }) =>
  (theme: ExactTheme) =>
    css({
      position: "absolute",
      width: "5px",
      height: "5px",
      top: "5px",
      right: "5px",
      backgroundColor: theme.colorsV2.grey[300],
      opacity: visible ? 1 : 0,
      borderRadius: "100%",
    });

export const plusButtonWrapper = css({
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  marginLeft: "5px",
});

export const plusButton =
  ({ disabled }: { disabled: boolean }) =>
  (theme: ExactTheme) => [
    css`
      border: none;
      background: transparent;
      cursor: ${disabled ? "not-allowed" : "pointer"};
      display: flex;
      align-items: center;
      position: relative;
      top: 1px;
      width: 25px;
      height: 25px;
      justify-content: center;
      border-radius: 13px;
      flex-shrink: 0;
      color: ${disabled ? theme.colorsV2.grey[500] : theme.colorsV2.fg.default};
    `,
    !disabled &&
      css`
        :hover {
          background: ${theme.colorsV2.grey[100]};
        }
        :focus-visible {
          background: ${theme.colorsV2.grey[200]};
        }
      `,
  ];

export const plusButtonText = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const plusButtonIcon = css`
  display: block;
  font-size: 12px;
`;
